import React, { useState, useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { createTaskAtom } from '../../../../atoms/v0/taskAtoms';
import { projectsAtom } from '../../../../atoms/v0/projectAtoms';
import { addRecentProjectIdAtom } from '../../../../atoms/v3/recentProjectsAtom';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { Project } from '../../../../types/project';
import { addDays, startOfDay, format } from 'date-fns';
import { trackTaskEvents } from '../../../../lib/analyticsEnhanced';
import { ProjectSelector } from '../tasks/ProjectSelector';

interface QuickAddBarV2Props {
  isOpen: boolean;
  onClose: () => void;
  defaultProjectId?: string | null;
  onTaskCreated?: () => void;
}

/**
 * ProjectV2 快速添加任务组件
 * 参考 v0 GroupBase 和 TaskQuickAdd 的设计，适配 ProjectV2 的视觉风格
 */
export const QuickAddBarV2: React.FC<QuickAddBarV2Props> = ({
  isOpen,
  onClose,
  defaultProjectId = null,
  onTaskCreated
}) => {
  const { t } = useTranslation(['v3/tasks', 'common']);
  const [isDark] = useAtom(isDarkAtom);
  const [, createTask] = useAtom(createTaskAtom);
  const [projects] = useAtom(projectsAtom);
  const [, addRecentProjectId] = useAtom(addRecentProjectIdAtom);
  const inputRef = useRef<HTMLInputElement>(null);

  // 表单状态
  const [title, setTitle] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(defaultProjectId);
  const [selectedStartAt, setSelectedStartAt] = useState<string | undefined>(undefined);

  // 当组件打开时自动聚焦输入框
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // 延迟聚焦，确保动画完成后再聚焦
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // 当默认项目ID变化时更新选中的项目
  useEffect(() => {
    setSelectedProjectId(defaultProjectId);
  }, [defaultProjectId]);

  // 键盘事件处理
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // 处理表单提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    try {
      const success = await createTask({
        title: trimmedTitle,
        projectId: selectedProjectId,
        startAt: selectedStartAt
      });

      if (success) {
        // 创建成功后清空输入框
        setTitle('');
        setSelectedStartAt(undefined);

        // 记录任务创建事件
        trackTaskEvents.taskCreated({
          title: trimmedTitle,
          projectId: selectedProjectId,
          startAt: selectedStartAt,
          from_quick_add: true,
          from_project_v2: true
        });

        // 如果有项目ID，添加到最近使用列表
        if (selectedProjectId) {
          addRecentProjectId(selectedProjectId);
        }

        // 通知父组件任务创建成功
        onTaskCreated?.();
        
        // 关闭快速添加界面
        onClose();
      }
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  // 处理项目选择
  const handleProjectSelect = (projectId: string | null) => {
    setSelectedProjectId(projectId);
  };

  // 处理今天按钮点击
  const handleTodayClick = () => {
    const today = startOfDay(new Date());
    setSelectedStartAt(today.toISOString());
  };

  // 处理明天按钮点击
  const handleTomorrowClick = () => {
    const tomorrow = startOfDay(addDays(new Date(), 1));
    setSelectedStartAt(tomorrow.toISOString());
  };

  // 清除日期选择
  const handleClearDate = () => {
    setSelectedStartAt(undefined);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="overflow-hidden"
      >
        <div className={`
          mx-4 my-3 rounded-lg border overflow-hidden
          ${isDark
            ? 'bg-ios-dark-gray6 border-ios-dark-gray4'
            : 'bg-gray-50 border-gray-200'
          }
        `}>
          <div className="px-4 py-3">
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* 任务标题输入框 */}
              <div className="flex items-center">
                <input
                  ref={inputRef}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('v3/tasks:quickAdd.placeholder', '添加新任务...')}
                  className={`
                    flex-grow text-sm bg-transparent border-none focus:outline-none focus:ring-0 py-1
                    ${isDark
                      ? 'placeholder-ios-dark-gray3 text-ios-dark-gray1'
                      : 'placeholder-gray-500 text-gray-900'
                    }
                  `}
                />
                <button
                  type="submit"
                  disabled={!title.trim()}
                  className={`
                    ml-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-200
                    ${title.trim()
                      ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-sm'
                      : isDark
                        ? 'bg-ios-dark-gray4 text-ios-dark-gray3 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }
                  `}
                >
                  {t('common:actions.add', '添加')}
                </button>
              </div>

              {/* 快速选项 */}
              <div className="space-y-3">
                {/* 项目选择器 */}
                <div className="flex flex-wrap gap-2">
                  <ProjectSelector
                    selectedProjectId={selectedProjectId}
                    onSelectProject={handleProjectSelect}
                  />
                </div>

                {/* 日期选择器 */}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleTodayClick}
                    className={`
                      px-3 py-1.5 text-xs rounded-lg font-medium transition-all duration-200
                      ${selectedStartAt && format(new Date(selectedStartAt), 'yyyy-MM-dd') === format(startOfDay(new Date()), 'yyyy-MM-dd')
                        ? 'bg-blue-500 text-white shadow-sm'
                        : isDark
                          ? 'bg-ios-dark-gray6 text-ios-dark-gray2 hover:bg-ios-dark-gray4'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }
                    `}
                  >
                    {t('v3/tasks:quickAdd.today', '今天')}
                  </button>
                  <button
                    type="button"
                    onClick={handleTomorrowClick}
                    className={`
                      px-3 py-1.5 text-xs rounded-lg font-medium transition-all duration-200
                      ${selectedStartAt && format(new Date(selectedStartAt), 'yyyy-MM-dd') === format(startOfDay(addDays(new Date(), 1)), 'yyyy-MM-dd')
                        ? 'bg-blue-500 text-white shadow-sm'
                        : isDark
                          ? 'bg-ios-dark-gray6 text-ios-dark-gray2 hover:bg-ios-dark-gray4'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }
                    `}
                  >
                    {t('v3/tasks:quickAdd.tomorrow', '明天')}
                  </button>
                  {selectedStartAt && (
                    <button
                      type="button"
                      onClick={handleClearDate}
                      className={`
                        px-3 py-1.5 text-xs rounded-lg font-medium transition-all duration-200
                        ${isDark
                          ? 'bg-ios-dark-gray6 text-ios-dark-gray2 hover:bg-ios-dark-gray4'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }
                      `}
                    >
                      {t('v3/tasks:quickAdd.clearDate', '清除日期')}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
